import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Urlaubsantrag bestaetigen"
 * (`accept-absence.component`, Desktop). Gestartet ueber den
 * `<app-dialog-help-button>` im Hero-Header (Pattern analog
 * `ABSENCE_ADD_HELP_TOUR`).
 *
 * Der Dialog oeffnet ausschliesslich aus einer datenabhaengigen Antragszeile
 * der Abwesenheits-Seite — es gibt keinen deterministischen Opener-Anker.
 * Daher KEIN `launchContext`: die Tour ist nur ueber den In-Dialog-`?`-Button
 * startbar (der den Dialog als bereits offen voraussetzt).
 *
 * Sieben Schritte (Body-Reihenfolge). `coverage`, `conflicts` und `quota` sind
 * bedingt sichtbar (Engpaesse / Schicht-Konflikte / Urlaub) und werden per
 * `skipIf` uebersprungen, wenn ihr Anker nicht im DOM ist. Keine Cues
 * (ein Spotlight-Bereich pro Schritt).
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

export const ABSENCE_ACCEPT_HELP_TOUR: TourConfig = {
  id: TourFeature.AbsenceAcceptHelp,
  version: 1,
  title: 'ABSENCE_ACCEPT_HELP_TOUR_TITLE',
  description: 'ABSENCE_ACCEPT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'ABSENCE_ACCEPT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.AbsenceAcceptDialog,
          spotlightAnchor: AnchorId.AbsenceAcceptDialog,
          title: 'TUTORIAL.ABSENCE_ACCEPT.WELCOME.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.WELCOME.CONTENT',
          placement: 'right',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'summary',
          anchor: AnchorId.AbsenceAcceptSummary,
          spotlightAnchor: AnchorId.AbsenceAcceptSummary,
          title: 'TUTORIAL.ABSENCE_ACCEPT.SUMMARY.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.SUMMARY.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'timeline',
          anchor: AnchorId.AbsenceAcceptTimeline,
          spotlightAnchor: AnchorId.AbsenceAcceptTimeline,
          title: 'TUTORIAL.ABSENCE_ACCEPT.TIMELINE.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.TIMELINE.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Mehrwochen-Kalender nur sichtbar, wenn der Antrag Wochen aufspannt.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="accept-absence-timeline"]'),
        },
        {
          id: 'coverage',
          anchor: AnchorId.AbsenceAcceptCoverage,
          spotlightAnchor: AnchorId.AbsenceAcceptCoverage,
          title: 'TUTORIAL.ABSENCE_ACCEPT.COVERAGE.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.COVERAGE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Engpass-Detail nur bei tatsaechlichen Besetzungsproblemen: `.aa-cov`
          // rendert auch ohne Engpaesse (nur "alle geprueft"-Fusszeile), daher
          // auf die Problem-Tag-Karten `.aa-covday` pruefen.
          skipIf: () => !document.querySelector('.aa-covday'),
        },
        {
          id: 'conflicts',
          anchor: AnchorId.AbsenceAcceptConflicts,
          spotlightAnchor: AnchorId.AbsenceAcceptConflicts,
          title: 'TUTORIAL.ABSENCE_ACCEPT.CONFLICTS.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.CONFLICTS.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Konflikt-Liste nur, wenn Schichten mit dem Urlaub kollidieren.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="accept-absence-conflicts"]'),
        },
        {
          id: 'quota',
          anchor: AnchorId.AbsenceAcceptQuota,
          spotlightAnchor: AnchorId.AbsenceAcceptQuota,
          title: 'TUTORIAL.ABSENCE_ACCEPT.QUOTA.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.QUOTA.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Jahres-Kontingent-Grid nur bei Urlaubsantraegen mit geladenen Stats.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="accept-absence-quota"]'),
        },
        {
          id: 'decision',
          anchor: AnchorId.AbsenceAcceptDecision,
          spotlightAnchor: AnchorId.AbsenceAcceptDecision,
          title: 'TUTORIAL.ABSENCE_ACCEPT.DECISION.TITLE',
          content: 'TUTORIAL.ABSENCE_ACCEPT.DECISION.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
